// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { TurnerProductInfo } from "@turnerBlueprint/App/Services/TurnerApiModels";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

/* CDT NOTE: This is a heavily customized widget to support Turner's presentation requirements. This should not be touched unless we verify a new feature or bugfix has been added to the base widget. */

type OwnProps = WidgetProps & HasProductContext & ReturnType<typeof mapStateToProps>;

const mapStateToProps = (state: ApplicationState) => ({
    productSettings: getSettingsCollection(state).productSettings,
});

export interface ProductDetailsPageTitleStyles {
    container?: GridContainerProps;
    brandGridItem?: GridItemProps;
    brandStyles?: ProductBrandStyles;
    titleGridItem?: GridItemProps;
    subInfoGridItem?: GridItemProps;
    titleText?: TypographyProps;
    brandTitleText?: TypographyProps;
    brandTitleLink?: LinkPresentationProps;
}

export const pageTitleStyles: ProductDetailsPageTitleStyles = {
    container: {
        gap: 0,
        offsetCss: css`
            max-width: 100%;
        `,
    },
    brandGridItem: {
        width: 12,
    },
    brandStyles: {
        logoImage: {
            css: css`
                img {
                    max-width: 150px;
                    max-height: 150px;
                }
            `,
        },
        nameText: {
            size: 16,
            weight: "normal",
        },
    },
    titleGridItem: {
        width: 12,
    },
    titleText: {
        variant: "h2",
    },
};

const styles = pageTitleStyles;

const ProductDetailsPageTitle: React.FC<OwnProps> = ({ productContext, productSettings }) => {
    const { product } = productContext;
    return (
        <GridContainer {...styles.container}>
            {product.brand && (
                <GridItem {...styles.brandGridItem}>
                    {product.brand.detailPagePath ? (
                        <Link {...styles.brandTitleLink} href={product!.brand?.detailPagePath}>
                            {product.brand.name}
                        </Link>
                    ) : (
                        <Typography {...styles.brandTitleText}>{product.brand.name}</Typography>
                    )}
                </GridItem>
            )}
            <GridItem {...styles.titleGridItem}>
                <Typography {...styles.titleText}>{productContext!.product!.productTitle}</Typography>
            </GridItem>
            {/** XNG-Changes: TODO */}
            {/* {!productContext!.product!.isVariantParent && turnerProductInfo!.brand !== undefined && (
                <>
                    {turnerProductInfo!.manufacturerItem && (
                        <GridItem {...styles.subInfoGridItem}>
                            <Typography {...styles.titleText}>
                                <strong>{translate("MFG #:")}</strong> {turnerProductInfo!.manufacturerItem}
                            </Typography>
                        </GridItem>
                    )}
                </>
            )}
            {productContext!.product !== undefined &&
                (productContext!.product!.isVariantParent ||
                    productContext!.product!.variantTraits === undefined ||
                    productContext!.product!.variantTraits!.length < 1) &&
                productContext!.product!.manufacturerItem && (
                    <GridItem {...styles.subInfoGridItem}>
                        <Typography {...styles.titleText}>
                            <strong>{translate("MFG #:")}</strong> {productContext!.product!.manufacturerItem}
                        </Typography>
                    </GridItem>
                )} */}
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withProductContext(ProductDetailsPageTitle)),
    definition: {
        displayName: "Page Title",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
